
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { IControl, ControlFacet } from '../../models/controls/ControlFacet';
import { IFaceted, unpack, pack, IFacetMapping } from '../../models/Facet';
import { IRenderProps, renderChildrenWithProps } from '../../utilities/components/RenderProps';
import { IComponentDecorator, withDisplayName, IDecoratedProps } from '../../utilities/components/Decorators';
import { mapObject } from '../../utilities/objects';
import { createMemoizer } from '../../utilities/Memoizer';

/**
 * @public
 */
export type IControlFaceted = IFaceted<IControl<any, any>>; // tslint:disable-line:no-any

/**
 * Interface for the control handlers available in the current React context.
 * @internal
 */
export interface IControlHandlerContext {
    controlHandlers?: IControlFaceted;
}

const NO_HANDLERS: IControlFaceted = pack({}, {});

/**
 * Validator for context types used by React.
 * @internal
 */
export const CONTROL_HANDLER_CONTEXT_TYPES: React.ValidationMap<IControlHandlerContext> = {
    controlHandlers: PropTypes.object
};

/**
 * The React context for current control handlers.
 * @internal
 */
export const CONTROL_HANDLER_CONTEXT: React.Context<IControlFaceted> = React.createContext(NO_HANDLERS);

/**
 * Default injected props for `withControlHandler`.
 * @public
 * @deprecated `withControlHandler` should no longer be used.
 */
export interface IControlHandlerProps<TProps, TOverrides> {
    /**
     * The control handler specified by the given control facet.
     */
    controlHandler: IControl<TProps, TOverrides>;
}

/**
 * Decorator which injects a control handler of the given type
 * into the target component.
 * @param controlTypeFacet - The control type which specifies the injection point and contract.
 * @public
 * @deprecated Components should use `itemCache.demandItemFacet` to access control handlers, even those from UX context.
 */
export function withControlHandler<TProps, TOverrides, TInjectedProps = IControlHandlerProps<TProps, TOverrides>>(
    controlTypeFacet: ControlFacet<TProps, TOverrides>,
    mapControlHandlerToProps?: (controlHandler: IControl<TProps, TOverrides>) => TInjectedProps
): IComponentDecorator<TInjectedProps, {}> {
    return <P extends TInjectedProps>(
        component: React.ComponentType<P>
    ): React.ComponentType<IDecoratedProps<P, TInjectedProps, {}>> => {
        const Component = component;

        class ControlHandlerConsumer extends React.PureComponent<IDecoratedProps<P, TInjectedProps, {}>> {
            public render(): JSX.Element {
                const {
                    props
                } = this;

                return (
                    <CONTROL_HANDLER_CONTEXT.Consumer>
                        { (controlHandlers: IControlFaceted) => {
                            const {
                                controlHandler = controlTypeFacet.default
                            } = unpack({
                                controlHandler: controlTypeFacet
                            }, controlHandlers);

                            return (
                                <Component
                                    { ...props }
                                    { ...mapControlHandlerToProps ? mapControlHandlerToProps(controlHandler) : {
                                        controlHandler: controlHandler
                                    } as {} as TInjectedProps }
                                />
                            );
                        } }
                    </CONTROL_HANDLER_CONTEXT.Consumer>
                );
            }
        }

        return ControlHandlerConsumer;
    };
}

/**
 * Decorator which injects a set of control handlers of given types
 * into the target component, using a facet mapping.
 * @param mapping - A mapping of prop names to the control facets to use for injection.
 * @deprecated Components should use `itemCache.demandItemFacet` to access control handlers, even those from UX context.
 * @public
 */
export function withControlHandlers<TControlFacets extends object>(
    mapping: IFacetMapping<TControlFacets>
): IComponentDecorator<TControlFacets, {}> {
    return <P extends TControlFacets>(
        component: React.ComponentType<P>
    ): React.ComponentType<IDecoratedProps<P, TControlFacets, {}>> => {
        const Component = component;

        class ControlHandlerConsumer extends React.PureComponent<IDecoratedProps<P, TControlFacets, {}>> {
            public render(): JSX.Element {
                const {
                    props
                } = this;

                return (
                    <CONTROL_HANDLER_CONTEXT.Consumer>
                        { (controlHandlers: IControlFaceted) => {
                            const controlFacets = {} as TControlFacets;

                            for (const key of (Object.keys(mapping) as (keyof TControlFacets)[])) {
                                const facet = mapping[key];

                                if (!facet) {
                                    throw new Error(`No facet mapped to '${key}'.`);
                                }

                                const {
                                    [facet.id]: controlHandler = ControlFacet.get(facet.id).default
                                } = controlHandlers;

                                controlFacets[key] = controlHandler as {} as TControlFacets[keyof TControlFacets];
                            }

                            return (
                                <Component
                                    { ...props }
                                    { ...controlFacets }
                                />
                            );
                        } }
                    </CONTROL_HANDLER_CONTEXT.Consumer>
                );
            }
        }

        return ControlHandlerConsumer;
    };
}

const controlHandlersMemoizer = createMemoizer((parentControlHandlers: IControlFaceted) => {
    return createMemoizer((childControlHandlers: IControlFaceted): IControlFaceted => {
        return {
            ...parentControlHandlers,
            ...mapObject(childControlHandlers, (handler: IControl<{}, {}>, key: string | number | symbol) => {
                // Compose new handlers with existing handlers.

                const control = ControlFacet.get(key as string);

                const parentHandler = control.unpack(parentControlHandlers);

                return parentHandler && control.compose(handler, parentHandler) || handler;
            })
        };
    });
});

/**
 * @public
 */
export interface IControlHandlerProviderProps {
    controlHandlers: IControlFaceted;
}

/**
 * Component which provides a set of control handlers to use within the wrapped component hierarchy.
 * All renderered components within the tree will respect the control handlers provided,
 * presuming they have a target control type and they are not overridden by a nested provider.
 * @public
 */
export class ControlHandlerProvider extends React.PureComponent<IControlHandlerProviderProps> {
    public render(): JSX.Element {
        const {
            controlHandlers: childControlHandlers,
            children
        } = this.props;

        return (
            <CONTROL_HANDLER_CONTEXT.Consumer>
                { (controlHandlers: IControlFaceted) => {
                    return (
                        <CONTROL_HANDLER_CONTEXT.Provider
                            value={ controlHandlersMemoizer(controlHandlers)(childControlHandlers) }
                        >
                            { children }
                        </CONTROL_HANDLER_CONTEXT.Provider>
                    );
                } }
            </CONTROL_HANDLER_CONTEXT.Consumer>
        );
    }
}

/**
 * Creates a render props component which provides control handlers for the given mapping.
 * @public
 * @deprecated Components should use `itemCache.demandItemFacet` to access control handlers, even those from UX context.
 */
export function createWithControlHandlers<TControlFacets extends object>(mapping: IFacetMapping<TControlFacets>):
    React.ComponentType<IRenderProps<TControlFacets>> & {
        propsType: TControlFacets;
    } {
    const component = withControlHandlers(mapping)(
        withDisplayName('WithControlHandlers.Callback')((props: TControlFacets & IRenderProps<TControlFacets>) => {
            const {
                children,
                ...renderProps
            } = props as IRenderProps<TControlFacets>;

            return renderChildrenWithProps(children, renderProps as TControlFacets);
        }));

    // @ts-ignore
    return withDisplayName('WithControlHandlers')(component);
}
